import { Token as SDKToken } from '@kodiak-finance/sdk-core'
import { PORTFOLIO_REFETCH_POLICY } from 'config/RefetchPolicies'
import { useConnection } from 'hooks/useConnection'
import { useTokenBalancesWithLoadingIndicator } from 'lib/hooks/useCurrencyBalance'
import { useMemo } from 'react'
import { ApiBault, ApiVaultData, BaultPosition, IslandPosition } from 'types/portfolio'
import { filterBaultPositions, filterFarmPositions, filterIslandPositions } from 'utils/portfolio/filterHelpers'
import { formatEther, getAddress } from 'viem'

import { useMasterVaultsData } from './useMasterData'
import { useUserFarmPositions } from './useUserFarmPositions'

export function useUserPositions(options?: {
  orderBy?: 'balance' | 'tvl' | 'apr' | 'totalApr'
  orderDirection?: 'asc' | 'desc'
  limit?: number
  enabled?: boolean
  filter?: string
}) {
  const { account, isConnected, chainId } = useConnection()
  const { data: allVaults, ...queryState } = useMasterVaultsData({
    enabled: isConnected && options?.enabled !== false,
  })

  const islandTokens = useMemo(
    () =>
      chainId && allVaults
        ? allVaults.map(
            (vault) =>
              new SDKToken(
                chainId,
                vault.id,
                vault.tokenLp?.decimals ?? 18,
                vault.tokenLp?.symbol ?? '',
                vault.tokenLp?.name ?? ''
              )
          )
        : [],
    [allVaults, chainId]
  )

  const [islandBalances] = useTokenBalancesWithLoadingIndicator(account, islandTokens, PORTFOLIO_REFETCH_POLICY)

  const baults: ApiBault[] = useMemo(() => {
    return (
      allVaults
        ?.flatMap((vault) => (vault.baults as ApiBault[]) || [])
        .filter((bault) => bault.blacklisted === false) || []
    )
  }, [allVaults])

  const baultTokens = useMemo(
    () => (chainId && baults ? baults.map((bault) => new SDKToken(chainId, bault.id, 18, '', '')) : []),
    [baults, chainId]
  )

  const [baultBalances] = useTokenBalancesWithLoadingIndicator(account, baultTokens, PORTFOLIO_REFETCH_POLICY)

  const islandPositions: IslandPosition[] = useMemo(() => {
    return (
      allVaults?.map((vault) => {
        const balance = islandBalances?.[getAddress(vault.id)]?.quotient.toString() || '0'
        const balanceFormatted = formatEther(BigInt(balance))
        const balanceUSD = Number(balanceFormatted) * (Number(vault.tokenLp?.price) || 0)
        return {
          vault,
          balance,
          balanceFormatted,
          balanceUSD,
        }
      }) || []
    )
  }, [allVaults, islandBalances])

  const baultPositions: BaultPosition[] = useMemo(() => {
    return (
      baults?.map((bault) => {
        const balance = baultBalances?.[getAddress(bault.id)]?.quotient.toString() || '0'
        const balanceFormatted = formatEther(BigInt(balance))
        const balanceUSD = Number(balanceFormatted) * (Number(bault.priceUSD) || 0)
        return {
          vault: allVaults?.find((vault) =>
            vault.baults?.some((_bault: ApiBault) => _bault.id === bault.id)
          ) as ApiVaultData,
          bault,
          balance,
          balanceFormatted,
          balanceUSD,
        }
      }) || []
    )
  }, [baults, baultBalances, allVaults])

  const {
    data: farmPositions,
    isLoading: isLoadingFarmPositions,
    refetchFarmPositions,
    refetchFarmRewards,
  } = useUserFarmPositions({
    enabled: isConnected && options?.enabled !== false,
  })

  // Filter positions first
  const filteredIslandPositions = filterIslandPositions(islandPositions, options?.filter || '')
  const filteredBaultPositions = filterBaultPositions(baultPositions, options?.filter || '')
  const filteredFarmPositions = filterFarmPositions(farmPositions || [], options?.filter || '')

  // Apply balance filters and sorting
  let sortedIslandPositions = filteredIslandPositions.filter((position) => Number(position.balance) > 0)
  let sortedBaultPositions = filteredBaultPositions.filter((position) => Number(position.balance) > 0)
  let sortedFarmPositions = filteredFarmPositions.filter((position) => position.stakedAmountUSD > 0)

  if (options?.orderBy) {
    const direction = options.orderDirection === 'asc' ? 1 : -1
    const compare = (aValue: number, bValue: number) => (aValue - bValue) * direction

    sortedIslandPositions.sort((a, b) => compare(a.balanceUSD || 0, b.balanceUSD || 0))
    sortedBaultPositions.sort((a, b) => compare(a.balanceUSD || 0, b.balanceUSD || 0))
    sortedFarmPositions.sort((a, b) => compare(a.stakedAmountUSD || 0, b.stakedAmountUSD || 0))
  }

  // Apply limit if requested
  if (options?.limit) {
    sortedIslandPositions = sortedIslandPositions.slice(0, options.limit)
    sortedBaultPositions = sortedBaultPositions.slice(0, options.limit)
    sortedFarmPositions = sortedFarmPositions.slice(0, options.limit)
  }

  return {
    ...queryState,
    data: {
      allIslandPositions: islandPositions.filter((position) => Number(position.balance) > 0),
      allBaultPositions: baultPositions.filter((position) => Number(position.balance) > 0),
      allFarmPositions: farmPositions?.filter((position) => position.stakedAmountUSD > 0) || [],
      sortedIslandPositions,
      sortedBaultPositions,
      sortedFarmPositions,
    },
    isLoading: queryState.isLoading || isLoadingFarmPositions,
    refetchFarmPositions,
    refetchFarmRewards,
  }
}
